/**
 * Performance utilities for frontend optimization
 */
import React from 'react';

// Debounce function for search inputs and API calls
export const debounce = (func, wait, immediate = false) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func(...args);
  };
};

// Throttle function for scroll events and frequent updates
export const throttle = (func, limit) => {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// Memoization for expensive calculations
export const memoize = (fn, getKey = (...args) => JSON.stringify(args)) => {
  const cache = new Map();
  return (...args) => {
    const key = getKey(...args);
    if (cache.has(key)) {
      return cache.get(key);
    }
    const result = fn(...args);
    cache.set(key, result);
    return result;
  };
};

// Lazy loading utility for components
export const lazyLoad = (importFunc, fallback = null) => {
  const LazyComponent = React.lazy(importFunc);

  return (props) => (
    <React.Suspense fallback={fallback || <div>Loading...</div>}>
      <LazyComponent {...props} />
    </React.Suspense>
  );
};

// Image lazy loading with intersection observer
export const createImageLazyLoader = () => {
  const imageObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target;
        img.src = img.dataset.src;
        img.classList.remove('lazy');
        observer.unobserve(img);
      }
    });
  });

  return {
    observe: (img) => imageObserver.observe(img),
    disconnect: () => imageObserver.disconnect(),
  };
};

// Virtual scrolling utility for large lists
export const useVirtualScrolling = (items, itemHeight, containerHeight) => {
  const [scrollTop, setScrollTop] = React.useState(0);

  const visibleStart = Math.floor(scrollTop / itemHeight);
  const visibleEnd = Math.min(
    visibleStart + Math.ceil(containerHeight / itemHeight) + 1,
    items.length
  );

  const visibleItems = items.slice(visibleStart, visibleEnd);
  const totalHeight = items.length * itemHeight;
  const offsetY = visibleStart * itemHeight;

  return {
    visibleItems,
    totalHeight,
    offsetY,
    onScroll: (e) => setScrollTop(e.target.scrollTop),
  };
};

// Performance monitoring utilities
export const performanceMonitor = {
  // Mark performance timing
  mark: (name) => {
    if (performance.mark) {
      performance.mark(name);
    }
  },

  // Measure performance between marks
  measure: (name, startMark, endMark) => {
    if (performance.measure) {
      performance.measure(name, startMark, endMark);
      const measure = performance.getEntriesByName(name)[0];
      return measure ? measure.duration : 0;
    }
    return 0;
  },

  // Get navigation timing
  getNavigationTiming: () => {
    if (performance.getEntriesByType) {
      const navigation = performance.getEntriesByType('navigation')[0];
      return {
        domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
        loadComplete: navigation.loadEventEnd - navigation.loadEventStart,
        firstPaint: performance.getEntriesByType('paint').find(entry => entry.name === 'first-paint')?.startTime,
        firstContentfulPaint: performance.getEntriesByType('paint').find(entry => entry.name === 'first-contentful-paint')?.startTime,
      };
    }
    return {};
  },

  // Monitor component render time
  measureRender: (componentName, renderFunction) => {
    const startMark = `${componentName}-render-start`;
    const endMark = `${componentName}-render-end`;

    performanceMonitor.mark(startMark);
    const result = renderFunction();
    performanceMonitor.mark(endMark);

    const duration = performanceMonitor.measure(`${componentName}-render`, startMark, endMark);

    if (duration > 16) { // More than one frame (16ms)
      console.warn(`Slow render detected for ${componentName}: ${duration.toFixed(2)}ms`);
    }

    return result;
  },
};

// Bundle size optimization utilities
export const bundleOptimization = {
  // Dynamic import with error handling
  dynamicImport: async (importPath) => {
    // try {
    //   const module = await import(importPath);
    //   return module;
    // } catch (error) {
    //   console.error(`Failed to load module: ${importPath}`, error);
    //   throw error;
    // }
    console.warn('Dynamic import is disabled to prevent webpack warnings');
    return Promise.reject(new Error('Dynamic import disabled'));
  },

  // Preload critical resources
  preloadResource: (href, as = 'script') => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = href;
    link.as = as;
    document.head.appendChild(link);
  },

  // Prefetch non-critical resources
  prefetchResource: (href) => {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = href;
    document.head.appendChild(link);
  },
};

// Memory management utilities
export const memoryManagement = {
  // Clean up event listeners
  cleanupEventListeners: (element, events) => {
    events.forEach(({ event, handler }) => {
      element.removeEventListener(event, handler);
    });
  },

  // Weak reference for avoiding memory leaks
  createWeakRef: (obj) => {
    if (typeof window !== 'undefined' && window.WeakRef) {
      return new window.WeakRef(obj);
    }
    return { deref: () => obj }; // Fallback for older browsers
  },

  // Monitor memory usage
  getMemoryUsage: () => {
    if (performance.memory) {
      return {
        used: Math.round(performance.memory.usedJSHeapSize / 1048576), // MB
        total: Math.round(performance.memory.totalJSHeapSize / 1048576), // MB
        limit: Math.round(performance.memory.jsHeapSizeLimit / 1048576), // MB
      };
    }
    return null;
  },
};

// Cache utilities for API responses
export const cacheUtils = {
  // Simple in-memory cache with TTL
  createCache: (defaultTTL = 300000) => { // 5 minutes default
    const cache = new Map();

    return {
      get: (key) => {
        const item = cache.get(key);
        if (!item) return null;

        if (Date.now() > item.expiry) {
          cache.delete(key);
          return null;
        }

        return item.value;
      },

      set: (key, value, ttl = defaultTTL) => {
        cache.set(key, {
          value,
          expiry: Date.now() + ttl,
        });
      },

      delete: (key) => cache.delete(key),
      clear: () => cache.clear(),
      size: () => cache.size,
    };
  },

  // Session storage cache
  sessionCache: {
    get: (key) => {
      try {
        const item = sessionStorage.getItem(key);
        return item ? JSON.parse(item) : null;
      } catch {
        return null;
      }
    },

    set: (key, value) => {
      try {
        sessionStorage.setItem(key, JSON.stringify(value));
      } catch (error) {
        console.warn('Failed to set session cache:', error);
      }
    },

    delete: (key) => sessionStorage.removeItem(key),
    clear: () => sessionStorage.clear(),
  },
};

// React performance hooks
export const usePerformanceOptimization = () => {
  // Stable callback reference
  const useStableCallback = React.useCallback;

  // Memoized value
  const useMemoizedValue = React.useMemo;

  // Debounced value
  const useDebouncedValue = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = React.useState(value);

    React.useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      return () => {
        clearTimeout(handler);
      };
    }, [value, delay]);

    return debouncedValue;
  };

  // Previous value tracking
  const usePrevious = (value) => {
    const ref = React.useRef();
    React.useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  };

  return {
    useStableCallback,
    useMemoizedValue,
    useDebouncedValue,
    usePrevious,
  };
};

const performanceUtils = {
  debounce,
  throttle,
  memoize,
  lazyLoad,
  createImageLazyLoader,
  useVirtualScrolling,
  performanceMonitor,
  bundleOptimization,
  memoryManagement,
  cacheUtils,
  usePerformanceOptimization,
};

export default performanceUtils;